import { useDisplayPrice, useDisplayPriceSum, type DisplayLineInput } from '@/hooks/useDisplayPrice';
import { formatPrice, formatPriceForCurrency } from '@/lib/formatPrice';
import { useStorefront } from '@/hooks/useStorefront';

/**
 * Renders a converted+rounded price as plain text (no wrapping element),
 * so it can drop-in replace `{formatPrice(x)}` without changing DOM.
 * CH: byte-identical to formatPrice(chf).
 * Non-CH: shows a skeleton while the rate loads and "—" on error/missing.
 */
export function DisplayPrice({
  chf,
  negative = false,
  withSpace = false,
  variationId,
}: {
  chf: number | null | undefined;
  negative?: boolean;
  withSpace?: boolean;
  /** Optional: variation id — enables per-country manual overrides (non-CH only). */
  variationId?: string | null;
}) {
  const { price, isLoading, isError } = useDisplayPrice(chf, variationId);
  const { storefront } = useStorefront();
  if (price) {
    let out = price.formatted;
    if (withSpace) {
      // CH fastpath must remain byte-identical to formatPrice(chf, undefined, true) ("CHF 9.90").
      if (storefront === 'ch') out = formatPrice(price.amount, undefined, true);
      else out = insertSpaceAfterSymbol(out, price.currency);
    }
    return <>{negative ? `-${out}` : out}</>;
  }
  if (isLoading) return <span className="inline-block h-4 w-14 animate-pulse rounded bg-muted align-middle" aria-hidden />;
  if (isError) return <>—</>;
  return <>—</>;
}

function insertSpaceAfterSymbol(formatted: string, currency: string): string {
  // EUR "€9,90" -> "€ 9,90", SEK "9,90 kr" already has a space, ALL "9,90 L" already spaced.
  if (currency === 'EUR' && formatted.startsWith('€') && formatted.charAt(1) !== ' ') {
    return `€ ${formatted.slice(1)}`;
  }
  return formatted;
}

/**
 * Renders the sum of pre-rounded line prices (see useDisplayPriceSum). Use
 * this instead of formatting a separately computed grand total so the
 * visible total equals the sum of the visible lines.
 * CH: byte-identical to formatPrice(sum of CHF values).
 */
export function DisplayPriceSum({
  chfValues,
  items,
}: {
  /** Legacy: bare CHF values (no overrides participate). */
  chfValues?: number[];
  /** Preferred: per-line {chf, variationId} so overrides feed the total. */
  items?: DisplayLineInput[];
}) {
  const input = items ?? (chfValues ?? []).map((v) => ({ chf: v }));
  const { sum, isLoading, isError } = useDisplayPriceSum(input);
  if (sum) return <>{sum.formatted}</>;
  if (isLoading) return <span className="inline-block h-4 w-14 animate-pulse rounded bg-muted align-middle" aria-hidden />;
  if (isError) return <>—</>;
  return <>—</>;
}